<template>
    <Page behind-nav class="home">
        <section class="procreate-hero procreate-hero--fade-out procreate-hero--content-bottom">
            <div class="procreate-hero__media">
                <img class="rmnp-hidden" :src="$asset($t('home.hero.video_poster'))" />
                <video
                    class="rm-hidden"
                    :src="$asset($t('home.hero.video'))"
                    :poster="$asset($t('home.hero.video_poster'))"
                    playsinline
                    muted
                    autoplay
                    loop
                ></video>
            </div>
            <div class="container">
                <div class="procreate-hero__content home-hero__content">
                    <div class="procreate-copy procreate-copy--center">
                        <h1 class="sr-only">{{ $t("home.title") }}</h1>
                        <p class="text-subheading">{{ $t("home.hero.subheading") }}</p>
                        <h2 class="text-heading text-heading--md" v-html="$t('home.hero.heading')"></h2>
                        <p class="text-body" v-html="$t('home.hero.body')"></p>
                        <div class="home-hero__awards">
                            <div v-for="(award, index) in $t('home.hero.awards')" :key="index" class="home-hero__award laurel-badge">
                                <img :src="$asset(award.badge)" :alt="award.title" height="51" />
                            </div>
                        </div>
                        <div class="home-hero__actions">
                            <ProButton :to="{ name: 'ipad' }" :aria-label="$t('home.hero.button_label')" pill variant="alt"
                                >{{ $t("global.learn_more") }}<ProIcon icon="cta-right"
                            /></ProButton>
                            <ProButton
                                v-ga-event:click="['leads', 'Send to App Store (Ipad)', 'Homepage Hero']"
                                v-fathom-event:click="['TYVOSEIS']"
                                href="https://apps.apple.com/app/apple-store/id425073498?pt=345446&ct=Website_Homepage&mt=8"
                                target="_blank"
                                pill
                                variant="primary"
                                >{{ $t("global.buy") }}<ProIcon icon="cta-right"
                            /></ProButton>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="home-container container">
            <div class="tile-grid tile-grid--top">
                <!-- iPad -->
                <div class="tile-grid-section tile-grid-section--full">
                    <ProLink
                        :to="{ name: 'ipad-whats-new' }"
                        :aria-label="$t('home.ipad.label')"
                        class="masonry-tile masonry-tile--procreate masonry-tile--full-layout"
                    >
                        <div class="masonry-tile__container">
                            <div class="masonry-tile__content" aria-hidden="true">
                                <h2 class="text-subheading">{{ $t("home.ipad.subheading") }}</h2>
                                <p class="text-heading" v-html="$t('home.ipad.heading')"></p>
                            </div>
                        </div>
                        <div
                            class="masonry-tile__bg mobile"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.ipad.image_mobile'))}')`,
                                backgroundSize: '130% auto',
                                backgroundPosition: 'bottom center',
                            }"
                        ></div>
                        <div
                            class="masonry-tile__bg tablet"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.ipad.image_mobile'))}')`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center!important',
                            }"
                        ></div>
                        <div
                            class="masonry-tile__bg desktop"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.ipad.image'))}')`,
                                backgroundSize: 'auto 100%',
                                backgroundPosition: 'left center',
                            }"
                        ></div>
                    </ProLink>
                </div>

                <!-- Alter Ego -->
                <div class="tile-grid-section tile-grid-section--two-third">
                    <ProLink
                        :to="{ name: 'events-alter-ego' }"
                        :aria-label="$t('home.alter_ego.label')"
                        class="masonry-tile"
                        :style="{ backgroundColor: '#0e0e0e' }"
                    >
                        <div class="masonry-tile__container">
                            <div class="masonry-tile__content" aria-hidden="true">
                                <h2 class="text-subheading">{{ $t("home.alter_ego.subheading") }}</h2>
                                <p class="text-heading" v-html="$t('home.alter_ego.heading')"></p>
                            </div>
                        </div>
                        <div
                            class="masonry-tile__bg desktop"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.alter_ego.image'))}')`,
                                backgroundPosition: 'left bottom',
                                backgroundSize: '101% auto',
                            }"
                        ></div>
                        <div
                            class="masonry-tile__bg tablet"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.alter_ego.image_tablet'))}')`,
                                backgroundPosition: 'left bottom',
                                backgroundSize: 'auto 100%',
                            }"
                        ></div>
                        <div
                            class="masonry-tile__bg mobile"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.alter_ego.image_mobile'))}')`,
                                backgroundPosition: 'left bottom',
                                backgroundSize: 'cover',
                            }"
                        ></div>
                    </ProLink>
                </div>

                <!-- Pocket -->
                <div class="tile-grid-section tile-grid-section--third">
                    <ProLink
                        :to="{ name: 'pocket' }"
                        :aria-label="$t('home.pocket.label')"
                        class="masonry-tile masonry-tile--pocket masonry-tile--text-center"
                        :style="{ backgroundColor: '#0f0f0f' }"
                    >
                        <div class="masonry-tile__container">
                            <div class="masonry-tile__content" aria-hidden="true">
                                <h2 class="text-subheading">{{ $t("home.pocket.subheading") }}</h2>
                                <p class="text-heading" v-html="$t('home.pocket.heading')"></p>
                            </div>
                        </div>

                        <div
                            class="masonry-tile__bg mobile"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.pocket.image_mobile'))}')`,
                                backgroundPosition: 'center top 16px',
                                backgroundSize: 'cover',
                            }"
                        >
                            <CreditMedia
                                :credit-only="true"
                                artist="Angela Sung"
                                artwork="On Tokyo streets"
                                :commission="true"
                                weighting="bottom"
                                :thumbnail="$asset('images/credit-thumbnails/pocket/angela-sung-on-tokyo-streets.jpg')"
                            />
                            <CreditMedia
                                :credit-only="true"
                                artist="Sara Lee"
                                artwork="Chromatic Jungle"
                                :commission="true"
                                :thumbnail="$asset('images/credit-thumbnails/pocket/sara-lee-chromatic-jungle.jpg')"
                            />
                        </div>

                        <div
                            class="masonry-tile__bg tablet"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.pocket.image_tablet'))}')`,
                                backgroundPosition: 'center center',
                                backgroundSize: 'cover',
                            }"
                        ></div>
                        <div
                            class="masonry-tile__bg desktop"
                            :style="{
                                backgroundImage: `url('${$asset($t('home.pocket.image'))}')`,
                                backgroundPosition: 'center top 16px',
                                backgroundSize: 'cover',
                            }"
                        ></div>
                    </ProLink>
                </div>
            </div>
        </section>

        <section class="home-container container">
            <div class="home-insight">
                <div class="home-insight__title-link">
                    <ProButton :to="{ name: 'insight' }" :aria-label="$t('home.insight.label')">
                        {{ $t("home.insight.subheading") }}<ProIcon icon="cta-right" />
                    </ProButton>
                    <h2 class="text-heading text-heading--sm" v-html="$t('home.insight.heading')"></h2>
                </div>
                <div class="home-insight__articles" v-if="latestArticles">
                    <InsightQuicklink v-for="relatedArticle in latestArticles" :key="relatedArticle.id" :article="relatedArticle" />
                </div>
            </div>
        </section>

        <section>
            <div class="home-container container">
                <div class="tile-grid tile-grid--bottom">
                    <!-- Beginners Series -->
                    <div class="tile-grid-section tile-grid-section--two-third">
                        <ProLink
                            :to="{ name: 'beginners-series' }"
                            :aria-label="$t('home.beginners_series.label')"
                            class="masonry-tile light-mode"
                            :style="{ backgroundColor: '#F7F6CB' }"
                        >
                            <div class="masonry-tile__container">
                                <div class="masonry-tile__content" aria-hidden="true">
                                    <h2 class="text-subheading">{{ $t("home.beginners_series.subheading") }}</h2>
                                    <p class="text-heading" v-html="$t('home.beginners_series.heading')"></p>
                                </div>
                            </div>
                            <div class="masonry-tile__bg">
                                <img class="rmnp-hidden" src="https://image.mux.com/g48ELw8sGW6To30164VmZoIJMyTXufp9JOiLnLZ5e4v00/thumbnail.png?time=0" />
                                <HlsVideo
                                    class="rm-hidden"
                                    poster="https://image.mux.com/g48ELw8sGW6To30164VmZoIJMyTXufp9JOiLnLZ5e4v00/thumbnail.png?time=0"
                                    :sources="[{ src: 'https://stream.mux.com/g48ELw8sGW6To30164VmZoIJMyTXufp9JOiLnLZ5e4v00.m3u8' }]"
                                    playsinline
                                    muted
                                    autoplay
                                    loop
                                    aria-hidden="true"
                                />
                            </div>
                        </ProLink>
                    </div>

                    <!-- Folio -->
                    <div class="tile-grid-section tile-grid-section--third">
                        <ProLink
                            v-ga-event:click="['leads', 'Send to Folio']"
                            href="https://folio.procreate.art"
                            target="_blank"
                            class="masonry-tile masonry-tile--gradient-short"
                            :style="{ backgroundColor: '#0524BF' }"
                            :aria-label="$t('home.folio.label')"
                        >
                            <div class="masonry-tile__container">
                                <div class="masonry-tile__content" aria-hidden="true">
                                    <h2 class="text-subheading">{{ $t("home.folio.subheading") }}</h2>
                                    <p class="text-heading" v-html="$t('home.folio.heading')"></p>
                                </div>
                            </div>
                            <div
                                class="masonry-tile__bg mobile"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.folio.image_mobile'))}')`,
                                    backgroundSize: 'cover',
                                }"
                            ></div>

                            <div
                                class="masonry-tile__bg tablet"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.folio.image_tablet'))}')`,
                                    backgroundSize: 'cover',
                                }"
                            ></div>
                            <div
                                class="masonry-tile__bg desktop"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.folio.image'))}')`,
                                    backgroundSize: 'cover',
                                }"
                            >
                                <CreditMedia
                                    :credit-only="true"
                                    artist="Sang Wook Kim"
                                    artwork="They Danced All Night"
                                    weighting="top"
                                    :thumbnail="$asset('images/credit-thumbnails/ipad/sang-wook-kim-the-danced-all-night.jpg')"
                                />
                            </div>
                        </ProLink>
                    </div>

                    <div class="tile-grid-section tile-grid-section--third tile-grid-section--sub-grid">
                        <!-- A11y -->
                        <ProLink
                            :to="{ name: 'ipad-accessibility' }"
                            :aria-label="$t('home.a11y.label')"
                            class="masonry-tile"
                            :style="{ backgroundColor: '#FED1C7' }"
                        >
                            <div class="masonry-tile__container">
                                <div class="masonry-tile__content light-mode" aria-hidden="true">
                                    <h2 class="text-subheading">{{ $t("home.a11y.subheading") }}</h2>
                                    <p class="text-heading" v-html="$t('home.a11y.heading')"></p>
                                </div>
                            </div>
                            <div
                                class="masonry-tile__bg desktop"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.a11y.image'))}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'left center',
                                }"
                            ></div>
                            <div
                                class="masonry-tile__bg tablet"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.a11y.image'))}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'left center',
                                }"
                            ></div>
                            <div
                                class="masonry-tile__bg mobile"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.a11y.image_mobile'))}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'bottom center',
                                }"
                            ></div>
                            <CreditMedia
                                :credit-only="true"
                                artist="Jhonny Núñez"
                                artwork="Desert"
                                :commission="true"
                                :thumbnail="$asset('images/credit-thumbnails/ipad/jhonny-nunez-desert.jpg')"
                            />
                        </ProLink>

                        <!-- Careers -->
                        <ProLink :to="{ name: 'careers' }" :aria-label="$t('home.careers.label')" class="masonry-tile" :style="{ backgroundColor: '#154328' }">
                            <div class="masonry-tile__container">
                                <div class="masonry-tile__content" aria-hidden="true">
                                    <h2 class="text-subheading">{{ $t("home.careers.subheading") }}</h2>
                                    <p class="text-heading" v-html="$t('home.careers.heading')"></p>
                                </div>
                            </div>
                            <div
                                class="masonry-tile__bg desktop"
                                :style="{
                                    backgroundImage: `url('${$asset('images/home/masonry/careers-tile.jpg')}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center right',
                                }"
                            ></div>
                            <div
                                class="masonry-tile__bg tablet"
                                :style="{
                                    backgroundImage: `url('${$asset('images/home/masonry/careers-tile.jpg')}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center right',
                                }"
                            ></div>
                            <div
                                class="masonry-tile__bg mobile"
                                :style="{
                                    backgroundImage: `url('${$asset('images/home/masonry/careers-tile-mobile.jpg')}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center right',
                                }"
                            ></div>
                        </ProLink>
                    </div>

                    <!-- EDU -->
                    <div class="tile-grid-section tile-grid-section--two-third tile-grid-section--full">
                        <ProLink
                            v-ga-event:click="['leads', 'Send to Education', 'Homepage Footer']"
                            href="//education.procreate.art"
                            target="_blank"
                            :aria-label="$t('home.education.label')"
                            class="masonry-tile"
                            :style="{ backgroundColor: '#033461' }"
                        >
                            <div class="masonry-tile__container">
                                <div class="masonry-tile__content" aria-hidden="true">
                                    <h2 class="text-subheading">{{ $t("home.education.subheading") }}</h2>
                                    <p class="text-heading" v-html="$t('home.education.heading')"></p>
                                </div>
                            </div>
                            <div
                                class="masonry-tile__bg desktop"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.education.image'))}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center center',
                                }"
                            ></div>
                            <div
                                class="masonry-tile__bg tablet"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.education.image_tablet'))}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center center',
                                }"
                            ></div>
                            <div
                                class="masonry-tile__bg mobile"
                                :style="{
                                    backgroundImage: `url('${$asset($t('home.education.image_mobile'))}')`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center center',
                                }"
                            ></div>
                        </ProLink>
                    </div>
                </div>
            </div>
        </section>

        <!-- <ArtistCredits /> -->
    </Page>
</template>

<script>
import { mapGetters } from "vuex";
import { parseISO, format } from "date-fns";

import LinkTile from "@/components/LinkTile";
import InsightLinkTile from "@/components/insight/InsightLinkTile";
import CreditMedia from "@/components/CreditMedia";
import ArtistCredits from "@/components/ArtistCredits";
import InsightQuicklink from "@/components/insight/InsightQuicklink";
import HlsVideo from "@/components/HlsVideo.vue";

export default {
    components: {
        InsightLinkTile,
        LinkTile,
        CreditMedia,
        ArtistCredits,
        InsightQuicklink,
        HlsVideo,
    },

    data: () => ({}),

    computed: {
        ...mapGetters({
            articles: "insight/getArticles",
            credits: "creditMedia/getMedia",
        }),
        latestArticles() {
            return this.articles.slice(0, 2);
        },
    },

    methods: {
        loadInsightPreviews() {
            if (this.$store.getters["insight/getShouldLoadInsightArticlesForLocaleCode"](this.$i18n.locale)) {
                this.$store.dispatch("insight/fetchArticles", { limit: 2 });
            }
        },
    },

    watch: {
        $route() {
            /**
             * Language probably changed so Insight
             * previews should be reloaded.
             */
            this.loadInsightPreviews();
        },
    },

    async mounted() {
        this.loadInsightPreviews();
    },

    head() {
        return {
            script: [
                {
                    type: "application/ld+json",
                    json: {
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        name: "Procreate",
                        url: "https://procreate.art/",
                        logo: "https://assets.procreate.art/img/procreate-icon-search-display.png",
                        sameAs: [
                            "https://www.facebook.com/procreate",
                            "https://twitter.com/Procreate",
                            "https://www.instagram.com/Procreate/",
                            "https://www.youtube.com/channel/UCU88WY0qX2bzDjjOLOj-Gng",
                            "https://wikipedia.org/wiki/Procreate_(software)",
                        ],
                    },
                },
            ],
            bodyAttrs: {
                class: "ultra-dark-mode",
            },
        };
    },
};
</script>
